"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const RULES = [
  {
    topic: "Admin only",
    rule: "Settings → Users and roles → Users. Title Users Management",
  },
  {
    topic: "Invite",
    rule: "Email Address + Role (from Roles). Sends an invite mail. Status starts Pending until they accept",
  },
  {
    topic: "Pending only: Resend and Delete",
    rule: "Resend Mail on a Pending row. Delete (confirm remove from the company) only while Pending. Active users cannot be deleted here",
  },
  {
    topic: "Edit",
    rule: "First Name, Last Name, Status (Active / Inactive; Pending stays Pending and the status picker is disabled), Role. If you are the signed-in admin, your own row cannot be edited or deleted. Tooltip on that case: Editing is disabled for admin users",
  },
  {
    topic: "What a role does",
    rule: "The Role picker is Settings → Roles. Changing role here is what the user can View / Create / Edit / Delete in Books. This screen does not define those permissions",
  },
];

const sections = [
  {
    heading: "What this is for",
    subsections: [
      { heading: "1. The list" },
      { heading: "2. Invite User" },
      { heading: "3. Update status and role" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

const Steps = ({ items }) => (
  <ol className="space-y-3 mb-4">
    {items.map((text, i) => (
      <li key={i} className="flex items-start gap-3 list-none">
        <span className="w-6 h-6 rounded-full bg-[#111827] text-white text-[12px] geist-medium flex items-center justify-center flex-shrink-0 mt-0.5">
          {i + 1}
        </span>
        <p className="text-[16px] geist-regular text-[#374151] leading-relaxed">
          {text}
        </p>
      </li>
    ))}
  </ol>
);

const Flow = ({ steps }) => (
  <p className="text-[15px] geist-regular text-[#374151] leading-relaxed mb-4 px-4 py-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-[10px]">
    {steps.map((step, i) => (
      <span key={`${step}-${i}`}>
        {i > 0 && (
          <span className="text-[#9CA3AF] geist-medium"> {"→"} </span>
        )}
        <span className="geist-medium text-[#111827]">{step}</span>
      </span>
    ))}
  </p>
);

export default function UsersPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Users
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Admins — who can sign in, which role they have, and whether
            they are active
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            Invite people to the company, wait until they accept, then set
            role and Active / Inactive. That is what gates every module they
            see. You do not design permissions on this screen — Roles does.
          </p>
          <Flow
            steps={[
              "Settings",
              "Users and roles",
              "Users",
              "Invite User",
              "email + Role",
              "they accept",
              "Edit if needed",
            ]}
          />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this is for
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. The list
          </h3>
          <Definitions
            items={[
              {
                term: "Columns",
                desc: "User Details (name), Email, Role, Status (Pending / Active / Inactive)",
              },
              {
                term: "What it affects",
                desc: "Who exists in the company. Inactive users cannot work the product as an Active user can. Role is which permission set they get from Roles",
              },
            ]}
          />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Invite User
          </h3>
          <Steps
            items={[
              <>
                Click <B>Invite User</B>.
              </>,
              <>
                Enter <B>Email Address</B> and pick <B>Role</B>. Click{" "}
                <B>Invite</B>.
              </>,
              <>
                They appear as <B>Pending</B>. Use <B>Resend</B> if the mail
                did not arrive. Delete only while still Pending.
              </>,
            ]}
          />
          <Callout type="note">
            Invites count against the user limit on Manage Subscription. If
            the plan is full, the invite fails until a seat is free.
          </Callout>

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Update status and role
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pencil opens <B>Update User Status and Role</B>. Header shows
            Name, Email, Status, Current Role.
          </p>
          <Steps
            items={[
              <>
                Edit <B>First Name</B> and <B>Last Name</B> (this is what My
                Profile shows for that person).
              </>,
              <>
                Set <B>Status</B> to Active or Inactive (not while they are
                still Pending). Set <B>Role</B>. Click <B>Update</B>.
              </>,
            ]}
          />
          <Callout type="warning">
            You cannot edit or delete your own admin row. Only Pending users
            can be removed from the company on this list.
          </Callout>
          <Screenshot caption="Users Management — Invite User, Pending with Resend, Edit status and role" />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[10px] overflow-hidden">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827] w-[28%]">
                    Topic
                  </th>
                  <th className="px-4 py-2.5 text-[13px] geist-medium text-[#111827]">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((row) => (
                  <tr
                    key={row.topic}
                    className="border-b border-[#E5E5E5] last:border-b-0"
                  >
                    <td className="px-4 py-2.5 text-[14px] geist-medium text-[#111827] align-top">
                      {row.topic}
                    </td>
                    <td className="px-4 py-2.5 text-[14px] geist-regular text-[#374151]">
                      {row.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/settings/roles"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Roles <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              Where View / Create / Edit / Delete per module is defined
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/my-profile"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              My Profile <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              What each user sees for their own name, email, phone, role
            </p>
          </Link>
          <Link
            href="/documentation/books/settings/manage-subscription"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Manage Subscription <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              User seat limit on the plan
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
